import { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

// SERVICES
import { getUserADInfo, getUserBDDInfo, insertUser, updateUser } from 'services/userService';
import { upsertUserPreferences, getDefaultPreferences, getUserPreferences } from 'services/tileService';
import { insertConnectionStat, insertUserConnectionStat } from 'services/statService';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

export default function useUserData(email, isInternal, isExternal, isAdmin) {
    const { addToast } = useContext(ToastContext);
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [userADInfo, setUserADInfo] = useState(null);
    const [userBDDInfo, setUserBDDInfo] = useState(null);
    const [preferences, setPreferences] = useState([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const fetchDefaultPreferences = async (user_id) => {
        try {
            const response_default_preferences = await getDefaultPreferences(isExternal ? 'extern' : 'intern');
            let preferencesData = response_default_preferences.data?.[0]?.DEFAULT_PREFERENCES;

            if (!preferencesData) {
                console.error('Erreur lors de la récupération des préférences par défaut :', response_default_preferences);
                addToast(
                    'Préférences par défaut',
                    'Erreur lors de la récupération des préférences par défaut.',
                    null,
                    null,
                    'error'
                );
                return;
            }

            if (typeof preferencesData === 'string') {
                try {
                    preferencesData = JSON.parse(preferencesData);
                } catch (error) {
                    console.error('Erreur lors de la récupération des préférences par défaut :', error);
                    addToast(
                        'Préférences par défaut',
                        'Erreur lors de la récupération des préférences par défaut.',
                        null,
                        null,
                        'error'
                    );
                    return;
                }
            }

            await upsertUserPreferences(user_id, preferencesData);
        } catch (error) {
            addToast(
                'Préférences par défaut',
                'Erreur lors de la récupération des préférences par défaut.',
                null,
                null,
                'error'
            );
            console.error('Erreur lors de la récupération des préférences par défaut :', error);
        }
    };

    const openIRRIS = () => {
        window.open('https://irris.altitudeinfra.fr/#/tickets/create', '_blank');
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response_user = await getUserADInfo(email);
                if (response_user) {
                    setUserADInfo(response_user);

                    const userExists = await getUserBDDInfo(response_user.data.ad_ai.uaiad_mail);

                    if (userExists.data.length === 0) {
                        let newUser = null;
                        try {
                            newUser = await insertUser(
                                response_user.data.ad_ai.uaiad_mail,
                                response_user.data.ad_ai.uaiad_physicaldeliveryofficename,
                                isExternal,
                                isAdmin,
                                'Google'
                            );
                            setUserBDDInfo(newUser);
                            setIsSuperAdmin(newUser.data[0].USR_IS_SUPER_ADMIN || false);
                        } catch (error) {
                            addToast(
                                'Données utilisateur',
                                'Erreur lors de l\'insertion des données utilisateur.',
                                null,
                                null,
                                'error'
                            );
                            console.error('Erreur lors de l\'insertion des données utilisateur :', error);
                        }

                        try {
                            await insertConnectionStat(newUser.data[0].USR_ID);
                        } catch (error) {
                            addToast(
                                'Statistiques',
                                'Erreur lors de l\'insertion de la statistique de connexion.',
                                null,
                                null,
                                'error'
                            );
                            console.error('Erreur lors de l\'insertion de la statistique de connexion :', error);
                        }

                        await fetchDefaultPreferences(newUser.data[0].USR_ID);
                        
                        try {
                            const response_preferences = await getUserPreferences(newUser.data[0].USR_ID);
                            setPreferences(response_preferences.data);
                        } catch (error) {
                            addToast(
                                'Préférences utilisateur',
                                'Erreur lors de la récupération des préférences utilisateur.',
                                null,
                                null,
                                'error'
                            );
                            console.error('Erreur lors de la récupération des préférences utilisateur :', error);
                        }
                    } else {
                        setUserBDDInfo(userExists);
                        setIsSuperAdmin(userExists.data[0].USR_IS_SUPER_ADMIN || false);

                        const lastConnection = new Date();
                        lastConnection.setHours(lastConnection.getHours() + 2);

                        await updateUser(response_user.data.ad_ai.uaiad_mail, {
                            last_connection_at: lastConnection,
                            is_external: isExternal,
                            is_admin: isAdmin,
                        });

                        await insertUserConnectionStat(userExists.data[0].USR_ID, lastConnection, location.pathname);

                        let response_preferences = await getUserPreferences(userExists.data[0].USR_ID);

                        if (response_preferences.data.length === 0) {
                            await fetchDefaultPreferences(userExists.data[0].USR_ID);
                            response_preferences = await getUserPreferences(userExists.data[0].USR_ID);
                        }

                        setPreferences(response_preferences.data);
                    }
                } else {
                    throw new Error('Utilisateur introuvable dans Active Directory.');
                }
            } catch (error) {
                addToast(
                    'Données utilisateur',
                    'Erreur lors de la récupération des données utilisateur.',
                    () => openIRRIS(),
                    'Ouvrir un ticket',
                    'error'
                );
                console.error('Erreur lors de la récupération des données utilisateur :', error);
            } finally {
                setLoading(false);
            }
        };

        if (email && (isInternal || isExternal || isAdmin)) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [email, isInternal, isExternal, isAdmin, location.pathname]);

    return { userADInfo, userBDDInfo, preferences, loading, isSuperAdmin };
}